import React, { useCallback, useEffect, useRef, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { ChatBubble } from "@/components/ChatBubble";
import { Search, RefreshCw, MessageSquare, X } from "lucide-react";

const STATUS_STYLE = {
  active: "bg-green-50 text-green-700 border-green-200",
  completed: "bg-blue-50 text-blue-700 border-blue-200",
  expired: "bg-gray-100 text-gray-500 border-gray-200",
};
const FILTERS = ["", "active", "completed", "expired"];
const dt = (iso) => (iso ? new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : "—");

const Kpi = ({ label, value, testid }) => (
  <div className="bg-white rounded-xl border border-gray-200 px-4 py-3" data-testid={testid}>
    <div className="text-xs uppercase tracking-wider text-gray-500 font-semibold">{label}</div>
    <div className="font-display text-2xl font-bold text-gray-900 mt-1">{value ?? "—"}</div>
  </div>
);

const Transcript = ({ sid, onClose }) => {
  const [s, setS] = useState(null);
  const endRef = useRef(null);
  useEffect(() => {
    const load = () => api.get(`/agent/sessions/${sid}`).then((r) => setS(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [sid]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [s?.messages?.length]);
  return (
    <div className="bg-white rounded-xl border border-gray-200 flex flex-col h-[36rem]" data-testid="agent-transcript">
      <div className="px-4 py-3 border-b border-gray-100 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-semibold text-gray-900 truncate" data-testid="agent-transcript-title">{s?.member_name || "New member"} · {s?.mobile}</div>
          <div className="text-xs text-gray-500">Step: {s?.state} · Claims: {s?.claims ?? 0}{s?.context?.code ? ` · Coupon ${s.context.code}` : ""}{s?.context?.dealer_name ? ` · ${s.context.dealer_name}` : ""}</div>
        </div>
        <button onClick={onClose} data-testid="agent-transcript-close" className="p-1 rounded hover:bg-gray-100"><X className="h-4 w-4" /></button>
      </div>
      <div className="flex-1 overflow-y-auto bg-[#E5DDD5] p-3 space-y-2">
        {s?.messages?.length === 0 && <div className="text-center text-sm text-gray-500 py-8">No messages yet</div>}
        {s?.messages?.map((m, i) => <ChatBubble key={i} m={m} testid={`agent-transcript-msg-${i}`} />)}
        <div ref={endRef} />
      </div>
    </div>
  );
};

export default function AgentSessions() {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [qInput, setQInput] = useState("");
  const [sel, setSel] = useState(null);
  useEffect(() => {
    const t = setTimeout(() => setQ(qInput.trim()), 400);
    return () => clearTimeout(t);
  }, [qInput]);

  const load = useCallback(() => {
    api.get("/agent/sessions", { params: { status, q, page_size: 50 } })
      .then((r) => setData(r.data)).catch((e) => toast.error(apiErr(e)));
  }, [status, q]);
  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load]);

  const st = data?.stats || {};
  return (
    <div>
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div><h1 className="font-display text-2xl sm:text-3xl font-bold text-gray-900">Agent Sessions</h1>
          <p className="text-sm text-gray-500 mt-1">Every WhatsApp conversation with the Jewel Paints Rewards bot · auto-refreshes every 5s</p></div>
        <button data-testid="agent-sessions-refresh" onClick={load} className="flex items-center gap-2 text-sm font-semibold border border-gray-300 px-3 py-2 rounded-lg hover:border-[#8B0000]"><RefreshCw className="h-4 w-4" /> Refresh</button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        <Kpi label="Total sessions" value={st.total} testid="agent-kpi-total" />
        <Kpi label="Active" value={st.active} testid="agent-kpi-active" />
        <Kpi label="Completed" value={st.completed} testid="agent-kpi-completed" />
        <Kpi label="Expired" value={st.expired} testid="agent-kpi-expired" />
        <Kpi label="Coupons claimed" value={st.claims} testid="agent-kpi-claims" />
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {FILTERS.map((f) => (
          <button key={f || "all"} data-testid={`agent-filter-${f || "all"}`} onClick={() => setStatus(f)}
            className={`px-3 py-1.5 rounded-full text-sm font-semibold capitalize ${status === f ? "bg-[#8B0000] text-white" : "bg-white border border-gray-200 text-gray-600 hover:border-[#8B0000]"}`}>{f || "All"}</button>
        ))}
        <div className="relative ml-auto">
          <Search className="h-4 w-4 absolute left-3 top-2.5 text-gray-400" />
          <input data-testid="agent-sessions-search" value={qInput} onChange={(e) => setQInput(e.target.value)} placeholder="Search mobile or name"
            className="pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:border-[#8B0000] w-60" />
        </div>
      </div>
      <div className={`grid gap-5 ${sel ? "lg:grid-cols-[1fr_26rem]" : ""}`}>
        <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
          <table className="w-full text-sm" data-testid="agent-sessions-table">
            <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
              <tr>{["Member", "Mobile", "Status", "Step", "Messages", "Claims", "Started", "Last activity"].map((h) => <th key={h} className="text-left px-4 py-3 font-semibold whitespace-nowrap">{h}</th>)}</tr>
            </thead>
            <tbody>
              {data?.items?.length === 0 && <tr><td colSpan={8} className="text-center text-gray-500 py-10" data-testid="agent-sessions-empty"><MessageSquare className="h-6 w-6 mx-auto mb-2 text-gray-300" />No sessions yet. They appear here as soon as someone messages the WhatsApp number.</td></tr>}
              {data?.items?.map((s) => (
                <tr key={s.id} data-testid={`agent-session-row-${s.id}`} onClick={() => setSel(s.id)}
                  className={`border-t border-gray-100 cursor-pointer hover:bg-red-50/40 ${sel === s.id ? "bg-red-50/60" : ""}`}>
                  <td className="px-4 py-3 font-medium text-gray-900 whitespace-nowrap">{s.member_name || "New member"}</td>
                  <td className="px-4 py-3 font-mono text-xs">{s.mobile}</td>
                  <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-bold border capitalize ${STATUS_STYLE[s.status] || ""}`}>{s.status}</span></td>
                  <td className="px-4 py-3 text-xs text-gray-600">{s.state}</td>
                  <td className="px-4 py-3">{s.message_count}</td>
                  <td className="px-4 py-3">{s.claims}</td>
                  <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">{dt(s.created_at)}</td>
                  <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">{dt(s.last_activity)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {sel && <Transcript sid={sel} onClose={() => setSel(null)} />}
      </div>
    </div>
  );
}
